import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import OnboardingPage from "@/pages/OnboardingPage";
import DashboardPage from "@/pages/DashboardPage";
import CategoryPage from "@/pages/CategoryPage";
import NotificationsPage from "@/pages/NotificationsPage";
import ProfilePage from "@/pages/ProfilePage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";
import { BlogList, BlogArticle } from "@/pages/BlogPage";
import FAQPage from "@/pages/FAQPage";
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage";
import AdminUsersPage from "@/pages/admin/AdminUsersPage";
import AdminUserDetailPage from "@/pages/admin/AdminUserDetailPage";
import AdminReportsPage from "@/pages/admin/AdminReportsPage";
import AdminAffiliatesPage from "@/pages/admin/AdminAffiliatesPage";
import AdminAnalyticsPage from "@/pages/admin/AdminAnalyticsPage";
import AdminLayout from "@/pages/admin/AdminLayout";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="w-10 h-10 bg-purple-200 rounded-xl flex items-center justify-center animate-pulse">
        <span className="text-white font-bold text-xl font-['Manrope']">B</span>
      </div>
    </div>
  );
  if (!user) return <Navigate to="/auth" replace />;
  return children;
};

const OnboardingGuard = ({ children }) => {
  const { user } = useAuth();
  if (user && !user.onboarding_completed) return <Navigate to="/onboarding" replace />;
  return children;
};

const AdminProtected = ({ children }) => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('brandos_admin_token') : null;
  if (!token) return <Navigate to="/skyportal" replace />;
  return children;
};

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="w-10 h-10 bg-purple-200 rounded-xl flex items-center justify-center animate-pulse">
        <span className="text-white font-bold text-xl font-['Manrope']">B</span>
      </div>
    </div>
  );

  return (
    <Routes>
      <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <LandingPage />} />
      <Route path="/auth" element={user ? <Navigate to="/dashboard" replace /> : <AuthPage />} />
      <Route path="/pricing" element={<Navigate to="/auth" replace />} />
      <Route path="/checkout/success" element={<Navigate to="/dashboard" replace />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/blog" element={<BlogList />} />
      <Route path="/blog/:slug" element={<BlogArticle />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><OnboardingGuard><DashboardPage /></OnboardingGuard></ProtectedRoute>} />
      <Route path="/category/:slug" element={<ProtectedRoute><OnboardingGuard><CategoryPage /></OnboardingGuard></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><OnboardingGuard><NotificationsPage /></OnboardingGuard></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><OnboardingGuard><ProfilePage /></OnboardingGuard></ProtectedRoute>} />

      {/* Admin Portal */}
      <Route path="/skyportal" element={<AdminLoginPage />} />
      <Route path="/skyportal/dashboard" element={<AdminProtected><AdminLayout><AdminDashboardPage /></AdminLayout></AdminProtected>} />
      <Route path="/skyportal/users" element={<AdminProtected><AdminLayout><AdminUsersPage /></AdminLayout></AdminProtected>} />
      <Route path="/skyportal/users/:userId" element={<AdminProtected><AdminLayout><AdminUserDetailPage /></AdminLayout></AdminProtected>} />
      <Route path="/skyportal/reports" element={<AdminProtected><AdminLayout><AdminReportsPage /></AdminLayout></AdminProtected>} />
      <Route path="/skyportal/affiliates" element={<AdminProtected><AdminLayout><AdminAffiliatesPage /></AdminLayout></AdminProtected>} />
      <Route path="/skyportal/analytics" element={<AdminProtected><AdminLayout><AdminAnalyticsPage /></AdminLayout></AdminProtected>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="noise-overlay" />
        <AppRoutes />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
